import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { t } from 'i18next';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CommonLoader } from '~components/Loaders/CommonLoader';
import { getMapStyles } from '~components/Maps/mapStyles';
import { makeGapOptions } from '~components/Maps/polylineOptions';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '~components/Maps/v2/constants';
import { DEFAULT_MAP_CENTER } from '~constants/mapConsts';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { TimeIndicatorContextProvider } from '~pages/LiveMap/Timeline/components/hooks/useTimeIndicatorContext';

import {
  catmullRomInterpolate,
  interpolateBearing,
  type Point,
} from './catmullRomSpline';
import { type JobTimelineEntry } from './DistanceJobsTimeline';
import { DistanceTimeline } from './DistanceTimeline';
import { splitPingsBySegments } from './getCycleSegmentBoundaries';
import { makeTruckMarkerIcon } from './timelineTicks';

const MAP_HEIGHT = 400;
const TRUCK_ICON_COLOR = '#006F12';

export function MapLoader() {
  return (
    <Box
      sx={{
        height: MAP_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CommonLoader />
    </Box>
  );
}

const POLYLINE_OPTIONS = {
  strokeColor: '#FF8C00',
  strokeOpacity: 0.8,
  strokeWeight: 3,
};

const DEFAULT_GAP_OPTIONS = makeGapOptions('#FF8C00');

interface Props {
  pings: LocationHistoryMinimal[];
  isLoading?: boolean;
  hideTimeline?: boolean;
  hideTruckMarker?: boolean;
  hidePolyline?: boolean;
  extraBounds?: { lat: number; lng: number }[];
  mapRef?: React.MutableRefObject<google.maps.Map | null>;
  onTimeChange?: (time: number) => void;
  jobs?: JobTimelineEntry[];
  children?: ReactNode;
}

export default function DistanceRouteMap({
  pings,
  isLoading,
  hideTimeline,
  hideTruckMarker,
  hidePolyline,
  extraBounds,
  mapRef: externalMapRef,
  onTimeChange,
  jobs,
  children,
}: Props) {
  const theme = useTheme();
  const internalMapRef = useRef<google.maps.Map | null>(null);
  const mapRef = externalMapRef ?? internalMapRef;
  const [highlightedMarker, setHighlightedMarker] = useState<{
    position: Point;
    bearing: number;
  } | null>(null);
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const rawPath = useMemo(() => {
    return pings
      .map((p) => ({
        lat: parseFloat(p.lat),
        lng: parseFloat(p.lon),
      }))
      .filter((p) => Math.abs(p.lat) > 0.01 || Math.abs(p.lng) > 0.01);
  }, [pings]);

  // Use the same segment boundaries as the timeline so gaps match exactly.
  const { solidSegments, gapSegments } = useMemo(
    () => splitPingsBySegments(pings),
    [pings],
  );

  // splitPingsBySegments returns already-splined points (the entire path is
  // splined as one curve then split), so no additional smoothing needed.
  const polylineSegments = solidSegments;
  const gapPolylines = gapSegments;

  // Flat path for bounds fitting (include gap points too)
  const allPoints = useMemo(
    () => [...polylineSegments.flat(), ...gapPolylines.flat()],
    [polylineSegments, gapPolylines],
  );

  const fitBounds = useCallback(
    (map: google.maps.Map) => {
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;

      for (const point of allPoints) {
        bounds.extend(point);
        hasPoints = true;
      }

      if (extraBounds) {
        for (const point of extraBounds) {
          bounds.extend(point);
          hasPoints = true;
        }
      }

      if (hasPoints) {
        map.fitBounds(bounds, 40);

        const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
          const zoom = map.getZoom();
          if (zoom !== undefined && zoom < 5) {
            map.setZoom(10);
          }
        });

        return () => window.google.maps.event.removeListener(listener);
      }
    },
    [allPoints, extraBounds],
  );

  useEffect(() => {
    if (mapRef.current && allPoints.length > 0) {
      fitBounds(mapRef.current);
    }
  }, [fitBounds, allPoints]);

  const defaultCenter = {
    lat: DEFAULT_MAP_CENTER.lat,
    lng: DEFAULT_MAP_CENTER.lng,
  };

  const handleTimelineHover = useCallback(
    (fractionalIndex: number) => {
      const bearing = interpolateBearing(rawPath, fractionalIndex);

      if (rawPath.length < 2) {
        const i = Math.round(fractionalIndex);
        if (i >= 0 && i < rawPath.length) {
          setHighlightedMarker({ position: rawPath[i], bearing });
        }
        return;
      }

      setHighlightedMarker({
        position: catmullRomInterpolate(rawPath, fractionalIndex),
        bearing,
      });
    },
    [rawPath],
  );

  if (!isLoaded) return null;

  if (!isLoading && pings.length === 0 && !children) {
    return (
      <Typography
        sx={{
          color: 'text.secondary',
          py: 1,
        }}
      >
        {t('admin.geo.map_no_location_data')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ height: MAP_HEIGHT, borderRadius: 1, overflow: 'hidden' }}>
        <GoogleMap
          mapContainerStyle={{ height: '100%', width: '100%' }}
          center={allPoints.length > 0 ? undefined : defaultCenter}
          zoom={allPoints.length > 0 ? undefined : 12}
          onLoad={(map) => {
            mapRef.current = map;
            fitBounds(map);
          }}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            mapTypeControl: true,
            gestureHandling: 'greedy',
            styles: getMapStyles(theme.palette.mode === 'dark'),
          }}
        >
          {!hidePolyline && (
            <>
              {polylineSegments.map((segment, i) =>
                segment.length > 1 ? (
                  <Polyline key={`seg-${i}`} path={segment} options={POLYLINE_OPTIONS} />
                ) : null,
              )}
              {gapPolylines.map((seg, i) =>
                seg.length > 1 ? (
                  <Polyline
                    key={`gap-seg-${i}`}
                    path={seg}
                    options={DEFAULT_GAP_OPTIONS}
                  />
                ) : null,
              )}
            </>
          )}

          {highlightedMarker && !hideTruckMarker && (
            <Marker
              position={highlightedMarker.position}
              icon={makeTruckMarkerIcon(TRUCK_ICON_COLOR, highlightedMarker.bearing)}
              zIndex={1000}
            />
          )}

          {children}
        </GoogleMap>
      </Box>

      {!hideTimeline && pings.length > 0 && (
        <TimeIndicatorContextProvider>
          <DistanceTimeline
            pings={pings}
            onHover={handleTimelineHover}
            onTimeChange={onTimeChange}
            jobs={jobs}
          />
        </TimeIndicatorContextProvider>
      )}
    </Box>
  );
}
